import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import axios from 'axios'
import { OrderItemCustomizations, MenuThemeId } from '../types'

// Use relative URLs so Vite proxy can intercept - no need for API_URL
const API_URL = ''

export interface CartItem {
  productId: string
  name: string
  price: number
  quantity: number
  imageUrl?: string
  customizations?: OrderItemCustomizations | null
  notes?: string
}

interface CartState {
  items: CartItem[]
  sessionCode: string | null
  customerId: string | null
  restaurantId: string | null
  menuTheme: MenuThemeId | null
  isLoading: boolean
  error: string | null

  // Computed values
  subtotal: () => number
  tax: () => number
  total: () => number
  itemCount: () => number

  // Actions
  setSession: (sessionCode: string, customerId: string, restaurantId: string, menuTheme?: MenuThemeId) => void
  setMenuTheme: (theme: MenuThemeId) => void
  addItem: (product: { id: string; name: string; price: number; imageUrl?: string }, quantity: number, customizations?: OrderItemCustomizations | null, notes?: string) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  updateItemNotes: (productId: string, notes: string) => void
  clearCart: () => void
  submitOrder: (orderNotes?: string, customerName?: string, skipSubmit?: boolean, paymentMethod?: string) => Promise<any>
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      sessionCode: null,
      customerId: null,
      restaurantId: null,
      menuTheme: null,
      isLoading: false,
      error: null,

      // Computed values
      subtotal: () => {
        return get().items.reduce((sum, item) => sum + (item.price * item.quantity), 0)
      },

      tax: () => {
        const subtotal = get().subtotal()
        return subtotal * 0.10 // 10% tax
      },

      total: () => {
        return get().subtotal() + get().tax()
      },

      itemCount: () => {
        return get().items.reduce((count, item) => count + item.quantity, 0)
      },

      // Actions
      setSession: (sessionCode: string, customerId: string, restaurantId: string, menuTheme?: MenuThemeId) => {
        set({ sessionCode, customerId, restaurantId, menuTheme: menuTheme || null, error: null })
      },

      setMenuTheme: (theme: MenuThemeId) => {
        set({ menuTheme: theme })
      },

      addItem: (product, quantity, customizations, notes) => {
        const state = get()

        // For customized products, always add as new item (don't merge)
        // For simple products, merge if same product
        let existingItemIndex = -1

        if (!customizations) {
          // Only try to find existing item if no customizations
          existingItemIndex = state.items.findIndex(
            item => item.productId === product.id && !item.customizations
          )
        }

        if (existingItemIndex >= 0) {
          // Update existing item quantity (only for non-customized items)
          const updatedItems = [...state.items]
          updatedItems[existingItemIndex].quantity += quantity
          set({ items: updatedItems })
        } else {
          // Add new item (always for customized products)
          const newItem: CartItem = {
            productId: product.id,
            name: product.name,
            price: product.price,
            quantity,
            imageUrl: product.imageUrl,
            customizations,
            notes
          }
          set({ items: [...state.items, newItem] })
        }
      },

      removeItem: (productId: string) => {
        set(state => ({
          items: state.items.filter(item => item.productId !== productId)
        }))
      },

      updateQuantity: (productId: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId)
          return
        }

        set(state => ({
          items: state.items.map(item =>
            item.productId === productId
              ? { ...item, quantity }
              : item
          )
        }))
      },

      updateItemNotes: (productId: string, notes: string) => {
        set(state => ({
          items: state.items.map(item =>
            item.productId === productId
              ? { ...item, notes }
              : item
          )
        }))
      },

      clearCart: () => {
        set({
          items: [],
          sessionCode: null,
          customerId: null,
          restaurantId: null,
          menuTheme: null,
          error: null
        })
      },

      submitOrder: async (orderNotes?: string, customerName?: string, skipSubmit = false, paymentMethod = 'CASH') => {
        const state = get()

        if (!state.sessionCode || !state.customerId) {
          set({ error: 'Session non valida. Scansiona il QR code nuovamente.' })
          throw new Error('Invalid session')
        }

        if (state.items.length === 0) {
          set({ error: 'Il carrello è vuoto' })
          throw new Error('Empty cart')
        }

        if (!customerName || !customerName.trim()) {
          set({ error: 'Inserisci il tuo nome per procedere' })
          throw new Error('Customer name required')
        }

        set({ isLoading: true, error: null })

        try {
          // Transform cart items to order items format expected by backend
          const orderItems = state.items.map(item => ({
            productId: item.productId,
            productName: item.name,
            quantity: item.quantity,
            unitPrice: item.price,
            customizations: item.customizations,
            notes: item.notes
          }))

          // Step 1: Create/update cart
          const cartResponse = await axios.post(`/api/public/cart`, {
            sessionCode: state.sessionCode,
            customerId: state.customerId,
            customerName: customerName.trim(),
            items: orderItems,
            notes: orderNotes,
            paymentMethod: paymentMethod
          })

          const order = cartResponse.data

          // Step 2: Submit order to kitchen (SKIP if payment required)
          let finalOrder = order
          if (!skipSubmit) {
            const submitResponse = await axios.post(`/api/public/order/${order.id}/submit`)
            finalOrder = submitResponse.data
          }

          // Clear cart on success
          set({
            items: [],
            isLoading: false,
            error: null
          })

          return finalOrder
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Errore durante l\'invio dell\'ordine'
          set({
            isLoading: false,
            error: errorMessage
          })
          throw error
        }
      }
    }),
    {
      name: 'qorder-cart-storage',
      // Only persist cart items and session info, not loading/error states
      partialize: (state) => ({
        items: state.items,
        sessionCode: state.sessionCode,
        customerId: state.customerId,
        restaurantId: state.restaurantId,
        menuTheme: state.menuTheme
      })
    }
  )
)
